import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
ChartJS.register(ArcElement, Tooltip, Legend);

// ─── constants ────────────────────────────────────────────────────────────────

const API        = 'http://csapi.compsoft.com.br:9009';
const AUTH       = { auth: { username: 'Portal', password: 'Portal1163' }, timeout: 15000 };
const MONTH_NAMES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
];
const CARD_W     = 230;
const CARD_H     = 290;
const CARD_GAP   = 10;
const STRIP_PAD  = 14;
const INIT_COUNT = 6;
const MORE_COUNT = 6;
const MAX_COUNT  = 24;

// ─── helpers ──────────────────────────────────────────────────────────────────

function normMY(year, month) {
  const d = new Date(Number(year), Number(month), 1);
  return { y: d.getFullYear(), m: d.getMonth() };
}

function buildMonthDays(year, month) {
  const days = [], d = new Date(Number(year), Number(month), 1);
  while (d.getMonth() === Number(month)) {
    days.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

function mergeWithAllDays(apiData, year, month) {
  return buildMonthDays(year, month).map((dateStr) => {
    const found = (apiData || []).find((i) => i.data && i.data.slice(0, 10) === dateStr);
    return found || { data: dateStr, vendas: 0, receber: 0, recebidas: 0, pagar: 0, pagas: 0, vendaContador: 0 };
  });
}

function sumField(data, field) {
  return (data || []).reduce((a, i) => a + (parseFloat(i[field]) || 0), 0);
}

function fmt(v) {
  return parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDay(d) { return d.slice(8, 10); }

// Um único request cobrindo todos os meses solicitados, agrupado por mês no cliente
async function fetchMultiMonth(cnpj, months) {
  const oldest  = months[months.length - 1];
  const newest  = months[0];
  const start   = new Date(oldest.year, oldest.month, 1).toISOString().slice(0, 10);
  const end     = new Date(newest.year, newest.month + 1, 0).toISOString().slice(0, 10);
  const res     = await axios.get(`${API}/dashboard/dados/${cnpj}/${start}/${end}`, AUTH);
  const rows    = Array.isArray(res.data) ? res.data : [];

  const grouped = {};
  months.forEach(({ key }) => { grouped[key] = []; });
  rows.forEach(item => {
    const dateStr = item.data?.slice(0, 10);
    if (!dateStr) return;
    const [y, m] = dateStr.split('-').map(Number);
    const key = `${y}-${m - 1}`;
    if (grouped[key] !== undefined) grouped[key].push(item);
  });
  return grouped;
}

// Fallback: um mês por vez, dias em lotes de 4
async function fetchDayByDay(cnpj, year, month) {
  const { y, m } = normMY(year, month);
  const today = new Date().toISOString().slice(0, 10);
  const days  = buildMonthDays(y, m).filter(d => d <= today);
  const results = [];
  for (let i = 0; i < days.length; i += 4) {
    const batch = await Promise.all(
      days.slice(i, i + 4).map(day =>
        axios.get(`${API}/dashboard/dados/${cnpj}/${day}`, AUTH)
          .then(res => (Array.isArray(res.data) ? res.data[0] : res.data) || null)
          .catch(() => null)
      )
    );
    results.push(...batch);
  }
  return results.filter(Boolean);
}

async function fetchMonthFallback(cnpj, year, month) {
  try {
    const rows = await (async () => {
      const { y, m } = normMY(year, month);
      const start = new Date(y, m, 1).toISOString().slice(0, 10);
      const end   = new Date(y, m + 1, 0).toISOString().slice(0, 10);
      const res   = await axios.get(`${API}/dashboard/dados/${cnpj}/${start}/${end}`, AUTH);
      return Array.isArray(res.data) ? res.data : [];
    })();
    return rows;
  } catch {
    try { return await fetchDayByDay(cnpj, year, month); } catch { return []; }
  }
}

// ─── FullMonthDetail ──────────────────────────────────────────────────────────

const DOW_NAMES  = ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'];
const DOW_ORDER  = [1, 2, 3, 4, 5, 6, 0]; // seg → dom
const DOW_COLORS = ['#E55010','#3b82f6','#22c55e','#a855f7','#f59e0b','#ec4899','#14b8a6'];

function parseDow(dateStr) {
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).getDay();
}

function FullMonthDetail({ data, year, month }) {
  const fullData = useMemo(() => mergeWithAllDays(data, year, month), [data, year, month]);

  const dowStats = useMemo(() => {
    const stats = {};
    fullData.forEach(item => {
      const v = parseFloat(item.vendas) || 0;
      const c = parseFloat(item.vendaContador) || 0;
      if (v === 0 && c === 0) return;
      const dow = parseDow(item.data);
      if (!stats[dow]) stats[dow] = { vendas: 0, contador: 0 };
      stats[dow].vendas   += v;
      stats[dow].contador += c;
    });
    return DOW_ORDER.map(d => ({
      name:     DOW_NAMES[d],
      vendas:   stats[d]?.vendas   || 0,
      contador: stats[d]?.contador || 0,
      ticket:   (stats[d]?.contador > 0) ? stats[d].vendas / stats[d].contador : 0,
    }));
  }, [fullData]);

  const thStyle = { whiteSpace: 'nowrap' };
  const sectionBox = (bg, border) => ({
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: 10,
    padding: '8px 6px',
    marginBottom: 10,
  });
  return (
    <div onClick={e => e.stopPropagation()}>
      {/* tabela diária */}
      <div style={sectionBox('rgba(59,130,246,0.07)', 'rgba(59,130,246,0.2)')}>
        <div style={{ fontSize: '0.67rem', fontWeight: 600, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
          <i className="fas fa-calendar-days" style={{ marginRight: 5 }} />Vendas por dia
        </div>
        <div style={{ overflowX: 'auto' }}>
        <table className="dash-table">
          <thead>
            <tr>
              <th style={{ ...thStyle, position: 'sticky', left: 0, zIndex: 3, background: 'var(--bg-card)' }}>Dia</th>
              <th style={thStyle}>Vendas R$</th>
              <th style={thStyle}>Qtd</th>
              <th style={thStyle}>Ticket Médio</th>
              <th style={thStyle}>Receber</th>
              <th style={thStyle}>Recebido</th>
              <th style={thStyle}>Pagar</th>
              <th style={thStyle}>Pago</th>
            </tr>
          </thead>
          <tbody>
            {fullData.map(item => {
              const has = parseFloat(item.vendas || 0) > 0 || parseFloat(item.receber || 0) > 0 || parseFloat(item.pagar || 0) > 0;
              return (
                <tr key={item.data} className={!has ? 'row-empty' : ''}>
                  <td style={{ position: 'sticky', left: 0, zIndex: 1, background: 'var(--bg-card)' }}>{fmtDay(item.data)}</td>
                  <td>{fmt(item.vendas)}</td>
                  <td>{Math.round(parseFloat(item.vendaContador) || 0)}</td>
                  <td>{fmt((parseFloat(item.vendaContador) > 0) ? parseFloat(item.vendas) / parseFloat(item.vendaContador) : 0)}</td>
                  <td>{fmt(item.receber)}</td>
                  <td>{fmt(item.recebidas)}</td>
                  <td>{fmt(item.pagar)}</td>
                  <td>{fmt(item.pagas)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ position: 'sticky', left: 0, zIndex: 1, background: 'var(--bg-card-alt)' }}>Total</td>
              <td>{fmt(sumField(fullData, 'vendas'))}</td>
              <td>{Math.round(sumField(fullData, 'vendaContador'))}</td>
              <td>{fmt(sumField(fullData, 'vendaContador') > 0 ? sumField(fullData, 'vendas') / sumField(fullData, 'vendaContador') : 0)}</td>
              <td>{fmt(sumField(fullData, 'receber'))}</td>
              <td>{fmt(sumField(fullData, 'recebidas'))}</td>
              <td>{fmt(sumField(fullData, 'pagar'))}</td>
              <td>{fmt(sumField(fullData, 'pagas'))}</td>
            </tr>
          </tfoot>
        </table>
        </div>
      </div>

      {/* vendas por dia da semana */}
      <div style={sectionBox('rgba(34,197,94,0.07)', 'rgba(34,197,94,0.2)')}>
        <div style={{ fontSize: '0.67rem', fontWeight: 600, color: '#4ade80', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
          <i className="fas fa-calendar-week" style={{ marginRight: 5 }} />
          Vendas por dia da semana
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="dash-table dash-table--compact">
            <thead>
              <tr>
                <th style={thStyle}>Dia</th>
                <th style={thStyle}>Vendas R$</th>
                <th style={thStyle}>Qtd</th>
                <th style={thStyle}>Ticket Médio</th>
              </tr>
            </thead>
            <tbody>
              {dowStats.map(({ name, vendas, contador, ticket }) => (
                <tr key={name} className={vendas === 0 ? 'row-empty' : ''}>
                  <td style={{ fontWeight: 600 }}>{name}</td>
                  <td>{fmt(vendas)}</td>
                  <td>{Math.round(contador)}</td>
                  <td>{fmt(ticket)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* gráfico de pizza */}
        {dowStats.some(d => d.vendas > 0) && (() => {
          const filtered = dowStats.map((d, i) => ({ ...d, color: DOW_COLORS[i] })).filter(d => d.vendas > 0);
          const total = filtered.reduce((a, d) => a + d.vendas, 0);
          const centerTextPlugin = {
            id: 'centerText',
            beforeDraw(chart) {
              const { ctx, chartArea } = chart;
              if (!chartArea) return;
              const cx = (chartArea.left + chartArea.right) / 2;
              const cy = (chartArea.top + chartArea.bottom) / 2;
              ctx.save();
              ctx.font = '600 10px Inter, system-ui, sans-serif';
              ctx.fillStyle = '#64748b';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText('Total', cx, cy - 9);
              ctx.font = 'bold 13px Inter, system-ui, sans-serif';
              ctx.fillStyle = '#f1f5f9';
              ctx.fillText(fmt(total), cx, cy + 9);
              ctx.restore();
            },
          };
          return (
            <div style={{ marginTop: 20, maxWidth: 280, margin: '20px auto 0' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10, textAlign: 'center' }}>
                <i className="fas fa-chart-pie" style={{ color: 'var(--primary-light)', marginRight: 6 }} />
                Distribuição por dia da semana
              </div>
              <Doughnut
                plugins={[centerTextPlugin]}
                data={{
                  labels: filtered.map(d => d.name),
                  datasets: [{
                    data: filtered.map(d => d.vendas),
                    backgroundColor: filtered.map(d => d.color + 'cc'),
                    borderColor: filtered.map(d => d.color),
                    borderWidth: 1.5,
                    hoverOffset: 6,
                  }]
                }}
                options={{
                  responsive: true,
                  cutout: '55%',
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: { color: '#94a3b8', font: { size: 11 }, padding: 10, boxWidth: 12 }
                    },
                    tooltip: {
                      callbacks: {
                        label: (ctx) => ` R$ ${fmt(ctx.raw)}`
                      }
                    }
                  }
                }}
              />
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ─── smooth SVG path through points ──────────────────────────────────────────

function smoothPath(pts) {
  if (pts.length < 2) return '';
  let d = `M ${pts[0].cx} ${pts[0].cy}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const mx = (pts[i].cx + pts[i + 1].cx) / 2;
    d += ` C ${mx} ${pts[i].cy} ${mx} ${pts[i + 1].cy} ${pts[i + 1].cx} ${pts[i + 1].cy}`;
  }
  return d;
}

// ─── SalesDataTable (componente principal) ────────────────────────────────────

function SalesDataTable({ selectedCnpj }) {
  const [monthsData, setMonthsData]   = useState({});
  const [expandedKey, setExpandedKey] = useState(null);
  const [retryKey, setRetryKey]       = useState(0);
  const [visible, setVisible]         = useState(INIT_COUNT);

  const prevRetryRef = useRef(0);
  const prevCnpjRef  = useRef('');
  const fetchedRef   = useRef(new Set());
  const scrollRef    = useRef(null);
  const drag         = useRef({ active: false, startX: 0, scrollLeft: 0, moved: false });

  const allMonths = useMemo(() => {
    const t = new Date();
    return Array.from({ length: MAX_COUNT }, (_, i) => {
      const { y, m } = normMY(t.getFullYear(), t.getMonth() - i);
      return { year: y, month: m, key: `${y}-${m}` };
    });
  }, []);

  const monthList = useMemo(() => allMonths.slice(0, visible), [allMonths, visible]);

  useEffect(() => {
    const cnpj = selectedCnpj || localStorage.getItem('cnpj-token');
    if (!cnpj) return;

    // reset on retry or cnpj switch
    if (retryKey !== prevRetryRef.current || cnpj !== prevCnpjRef.current) {
      prevRetryRef.current = retryKey;
      prevCnpjRef.current  = cnpj;
      fetchedRef.current   = new Set();
      setMonthsData({});
    }

    const toFetch = monthList.filter(({ key }) => !fetchedRef.current.has(key));
    if (toFetch.length === 0) return;

    toFetch.forEach(({ key }) => fetchedRef.current.add(key));

    const loading = {};
    toFetch.forEach(({ key }) => { loading[key] = { data: [], loading: true }; });
    setMonthsData(prev => ({ ...prev, ...loading }));

    // tenta buscar todos os meses em 1 request; fallback individual se falhar
    (async () => {
      try {
        const grouped = await fetchMultiMonth(cnpj, toFetch);
        toFetch.forEach(({ key }) =>
          setMonthsData(prev => ({ ...prev, [key]: { data: grouped[key] || [], loading: false } }))
        );
      } catch {
        for (let i = 0; i < toFetch.length; i += 2) {
          await Promise.all(
            toFetch.slice(i, i + 2).map(({ year, month, key }) =>
              fetchMonthFallback(cnpj, year, month)
                .then(data => setMonthsData(prev => ({ ...prev, [key]: { data, loading: false } })))
                .catch(()  => setMonthsData(prev => ({ ...prev, [key]: { data: [], loading: false, error: true } })))
            )
          );
        }
      }
    })();
  }, [selectedCnpj, retryKey, monthList]);

  const maxVendas = useMemo(() => {
    const vals = monthList.map(({ key }) => sumField(monthsData[key]?.data, 'vendas'));
    return Math.max(1, ...vals);
  }, [monthsData, monthList]);

  // SVG points — centered horizontally per card, vertically based on sales
  const midY      = CARD_H * 0.55;
  const amplitude = CARD_H * 0.14;

  const svgPoints = useMemo(() => {
    return monthList.map(({ key }, i) => {
      const cx = STRIP_PAD + i * (CARD_W + CARD_GAP) + CARD_W / 2;
      const v  = sumField(monthsData[key]?.data, 'vendas');
      const cy = midY + amplitude - (v / maxVendas) * (amplitude * 2);
      return { cx, cy, v };
    });
  }, [monthsData, maxVendas, monthList]);

  const hasMore = visible < MAX_COUNT;
  const stripW  = STRIP_PAD * 2 + visible * (CARD_W + CARD_GAP) - CARD_GAP + (hasMore ? CARD_W / 2 + CARD_GAP : 0);

  const linePath = smoothPath(svgPoints);

  // area path: close below the card strip
  const areaPath = svgPoints.length > 0
    ? `${linePath} L ${svgPoints[svgPoints.length - 1].cx} ${CARD_H} L ${svgPoints[0].cx} ${CARD_H} Z`
    : '';

  const expandedState = expandedKey ? (monthsData[expandedKey] || {}) : null;
  const expandedEntry = expandedKey ? monthList.find(m => m.key === expandedKey) : null;

  const onDragDown = (e) => {
    if (e.button !== 0) return;
    drag.current = { active: true, startX: e.clientX, scrollLeft: scrollRef.current.scrollLeft, moved: false };
    scrollRef.current.style.cursor = 'grabbing';
  };
  const onDragMove = (e) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 4) {
      drag.current.moved = true;
      scrollRef.current.scrollLeft = drag.current.scrollLeft - dx;
    }
  };
  const onDragEnd = () => {
    drag.current.active = false;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grab';
  };

  const onTouchStart = (e) => {
    const t = e.touches[0];
    drag.current = { active: true, startX: t.clientX, scrollLeft: scrollRef.current.scrollLeft, moved: false };
  };
  const onTouchMove = (e) => {
    if (!drag.current.active) return;
    if (Math.abs(e.touches[0].clientX - drag.current.startX) > 4) drag.current.moved = true;
  };
  const onTouchEnd = () => { drag.current.active = false; };

  return (
    <div className="dash-card" style={{ padding: 0, overflow: 'hidden' }}>

      {/* cabeçalho */}
      <div style={{ padding: '14px 18px 10px', borderBottom: '1px solid var(--border-soft)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="section-title" style={{ marginBottom: 0 }}>
          <i className="fas fa-chart-line icon-orange" /> Evolução Mensal
        </div>
        <button className="btn-outline-dash" style={{ fontSize: '0.75rem' }} onClick={() => setRetryKey(k => k + 1)}>
          <i className="fas fa-rotate-right me-1" />Atualizar
        </button>
      </div>

      {/* faixa de cards — sem scrollbar visível */}
      <div
        ref={scrollRef}
        className="cards-scroll"
        style={{ overflowX: 'auto', padding: `${STRIP_PAD}px 0`, cursor: 'grab', userSelect: 'none', touchAction: 'pan-x' }}
        onMouseDown={onDragDown}
        onMouseMove={onDragMove}
        onMouseUp={onDragEnd}
        onMouseLeave={onDragEnd}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div style={{ position: 'relative', width: stripW, height: CARD_H, padding: `0 ${STRIP_PAD}px` }}>

          {/* cards */}
          <div style={{ display: 'flex', gap: CARD_GAP, height: '100%' }}>
            {monthList.map(({ year, month, key }, index) => {
              const state      = monthsData[key] || { data: [], loading: true };
              const data       = state.data;
              const isSelected = expandedKey === key;
              const isCurrent  = index === 0;

              const totalVendas  = sumField(data, 'vendas');
              const totalContador= sumField(data, 'vendaContador');
              const countDias    = data.filter(i => parseFloat(i.vendas || 0) > 0).length;
              const ticketMedio  = totalContador > 0 ? totalVendas / totalContador : 0;
              const mediaPorDia  = countDias > 0 ? totalVendas / countDias : 0;
              const totalRec     = sumField(data, 'recebidas');
              const totalARec    = sumField(data, 'receber');
              const totalPago    = sumField(data, 'pagas');
              const totalAPagar  = sumField(data, 'pagar');

              return (
                <div
                  key={key}
                  style={{
                    flexShrink: 0,
                    width: CARD_W,
                    height: CARD_H,
                    background: isSelected ? 'rgba(229,80,16,0.08)' : 'var(--bg-card)',
                    border: `1px solid ${isSelected ? 'var(--primary-light)' : 'var(--border-soft)'}`,
                    borderRadius: 12,
                    cursor: 'pointer',
                    transition: 'border-color 0.2s, background 0.2s',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    zIndex: 1,
                  }}
                  onClick={() => { if (drag.current.moved) { drag.current.moved = false; return; } setExpandedKey(isSelected ? null : key); }}
                >
                  {/* topo */}
                  <div style={{ padding: '12px 14px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.97rem', color: '#f1f5f9', lineHeight: 1.2 }}>
                        {MONTH_NAMES[month]}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{year}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      {isCurrent && (
                        <span style={{ fontSize: '0.63rem', fontWeight: 700, color: '#E55010', background: 'rgba(229,80,16,0.12)', borderRadius: 8, padding: '2px 6px', letterSpacing: '0.04em' }}>
                          ATUAL
                        </span>
                      )}
                      <i className={`fas fa-chevron-${isSelected ? 'up' : 'down'}`} style={{ color: '#475569', fontSize: 10 }} />
                    </div>
                  </div>

                  {state.loading ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: '0.83rem' }}>
                      <i className="fas fa-circle-notch fa-spin me-1" />Carregando
                    </div>
                  ) : (
                    <div style={{ flex: 1, padding: '4px 14px 12px', display: 'flex', flexDirection: 'column' }}>
                      {/* Vendas — destaque */}
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 1 }}>Vendas</div>
                        <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#E55010', letterSpacing: '-0.01em', lineHeight: 1.1 }}>
                          {fmt(totalVendas)}
                        </div>
                      </div>

                      {/* outros — linha label: valor */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        {[
                          { label: 'Ticket Médio', value: fmt(ticketMedio) },
                          { label: 'Dias ativos',  value: `${countDias} dias` },
                          { label: 'Média/dia',    value: fmt(mediaPorDia) },
                          { label: 'Recebido',     value: fmt(totalRec)    },
                          { label: 'A Receber',    value: fmt(totalARec)   },
                          { label: 'Pago',         value: fmt(totalPago)   },
                          { label: 'A Pagar',      value: fmt(totalAPagar) },
                        ].map(({ label, value }) => (
                          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 4 }}>
                            <span style={{ fontSize: '0.77rem', color: '#64748b', flexShrink: 0 }}>{label}</span>
                            <span style={{ fontSize: '0.83rem', fontWeight: 600, color: '#94a3b8', textAlign: 'right' }}>{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* botão load more */}
            {hasMore && (
              <div
                style={{
                  flexShrink: 0,
                  width: CARD_W / 2,
                  height: CARD_H,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  border: '1px dashed rgba(255,255,255,0.1)',
                  borderRadius: 12,
                  cursor: 'pointer',
                  color: '#475569',
                  fontSize: '0.75rem',
                  transition: 'border-color 0.2s, color 0.2s',
                  position: 'relative',
                  zIndex: 1,
                }}
                className="load-more-card"
                onClick={() => { if (drag.current.moved) { drag.current.moved = false; return; } setVisible(v => Math.min(v + MORE_COUNT, MAX_COUNT)); }}
              >
                <i className="fas fa-chevron-right" style={{ fontSize: 18 }} />
                <span>Ver mais<br />6 meses</span>
              </div>
            )}
          </div>

          {/* SVG sobreposto — linha gráfica no centro dos cards */}
          <svg
            width={stripW - STRIP_PAD * 2}
            height={CARD_H}
            style={{ position: 'absolute', top: 0, left: STRIP_PAD, pointerEvents: 'none', zIndex: 2 }}
          >
            <defs>
              <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%"   stopColor="rgba(229,80,16,0)" />
                <stop offset="15%"  stopColor="rgba(229,80,16,0.28)" />
                <stop offset="85%"  stopColor="rgba(229,80,16,0.28)" />
                <stop offset="100%" stopColor="rgba(229,80,16,0)" />
              </linearGradient>
              <linearGradient id="areaGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%"   stopColor="rgba(229,80,16,0.05)" />
                <stop offset="100%" stopColor="rgba(229,80,16,0)" />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="1.5" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>

            {/* área preenchida abaixo da linha */}
            {areaPath && <path d={areaPath} fill="url(#areaGrad)" />}

            {/* linha suave com glow */}
            {linePath && (
              <path d={linePath} stroke="url(#lineGrad)" strokeWidth="1.5" fill="none" filter="url(#glow)" strokeLinecap="round" strokeLinejoin="round" />
            )}

            {/* pontos em cada card */}
            {svgPoints.map(({ cx, cy }, i) => (
              <g key={i}>
                <circle cx={cx} cy={cy} r={i === 0 ? 6 : 4} fill={i === 0 ? '#E55010' : 'rgba(229,80,16,0.6)'} />
                {i === 0 && <circle cx={cx} cy={cy} r={10} fill="none" stroke="rgba(229,80,16,0.25)" strokeWidth="2" />}
              </g>
            ))}
          </svg>

        </div>
      </div>

      {/* painel expandido */}
      {expandedEntry && expandedState && !expandedState.loading && (
        <div style={{ borderTop: '1px solid var(--border-soft)', padding: '12px 6px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#f1f5f9' }}>
              {MONTH_NAMES[expandedEntry.month]} {expandedEntry.year}
            </span>
            <button className="btn-outline-dash" style={{ fontSize: '0.72rem' }} onClick={() => setExpandedKey(null)}>
              <i className="fas fa-xmark me-1" />Fechar
            </button>
          </div>
          <FullMonthDetail data={expandedState.data} year={expandedEntry.year} month={expandedEntry.month} />
        </div>
      )}
    </div>
  );
}

export default SalesDataTable;
